import React from 'react';
import { Link } from 'react-router-dom';
import { useNearbyDisplays } from '@/hooks/useNearbyDisplays';
import { getSeoLocationComponents } from '@/lib/locationParser';

interface DisplaySeoFooterProps {
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
  displayId: string;
  location?: string;
}

interface SeoLink {
  label: string;
  url: string;
  external?: boolean;
}

/**
 * Generate nearby city links with improved filtering and variety
 */
function getNearbyCityLinks(cities: string[], currentCity: string, state: string): SeoLink[] {
  if (!Array.isArray(cities) || !currentCity || !state) {
    return [];
  }

  // Filter out current city and empty values, then deduplicate
  const uniqueCities = Array.from(new Set(
    cities
      .filter(city => city && typeof city === 'string' && city.trim() !== '')
      .map(city => city.trim())
      .filter(city => city.toLowerCase() !== currentCity.toLowerCase())
  ));

  // Take up to 4 cities for more variety
  return uniqueCities.slice(0, 4).map(city => ({
    label: `Light Displays in ${city}`,
    url: `/displays?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`,
    external: false
  }));
}

/**
 * Generate "Best of City, State" links with enhanced variety
 */
function getBestOfLinks(city: string, state: string): SeoLink[] {
  if (!city || !state) return [];

  return [
    { 
      label: `Christmas Light Displays in ${city}`, 
      url: `/displays?city=${encodeURIComponent(city)}&holiday=christmas`,
      external: false
    },
    { 
      label: `Halloween Displays in ${city}`, 
      url: `/displays?city=${encodeURIComponent(city)}&holiday=halloween`,
      external: false
    },
    { 
      label: `Musical Light Shows in ${city}`, 
      url: `/displays?city=${encodeURIComponent(city)}&type=musical`,
      external: false
    },
    { 
      label: `Drive-Thru Displays in ${state}`, 
      url: `/displays?state=${encodeURIComponent(state)}&type=drive-thru`,
      external: false
    },
    { 
      label: `Family-Friendly Light Shows in ${city}`, 
      url: `/displays?city=${encodeURIComponent(city)}&type=family`,
      external: false
    },
  ];
}

/**
 * Generate "Browse Nearby" links with improved targeting
 */
function getBrowseNearbyLinks(city: string, state: string, zip?: string): SeoLink[] {
  if (!city || !state) return [];

  const baseLinks: SeoLink[] = [
    { 
      label: `Light Displays near ${city}`, 
      url: `/displays/nearby?city=${encodeURIComponent(city)}`,
      external: false
    },
    { 
      label: `Synchronized Shows near ${city}`, 
      url: `/displays/nearby?city=${encodeURIComponent(city)}&type=synchronized`,
      external: false
    },
    { 
      label: `${city} Christmas Lights Map`, 
      url: `/map?city=${encodeURIComponent(city)}`,
      external: false
    },
  ];

  // Add ZIP-based link if available
  if (zip && zip.trim()) {
    baseLinks.push({
      label: `Holiday Displays near ${zip}`, 
      url: `/displays/nearby?zip=${encodeURIComponent(zip)}`,
      external: false
    });
  }

  return baseLinks;
}

/**
 * Generate "Trending Searches" links with state focus
 */
function getTrendingLinks(state: string): SeoLink[] {
  if (!state) return [];

  return [
    { 
      label: `Best Christmas Lights in ${state}`, 
      url: `/displays?state=${encodeURIComponent(state)}&holiday=christmas`,
      external: false
    },
    { 
      label: `Halloween Light Tours ${state}`, 
      url: `/displays?state=${encodeURIComponent(state)}&holiday=halloween`,
      external: false
    },
    { 
      label: `Musical Light Displays in ${state}`, 
      url: `/displays?state=${encodeURIComponent(state)}&type=musical`,
      external: false
    },
    { 
      label: `FM Radio Synced Displays in ${state}`, 
      url: `/displays?state=${encodeURIComponent(state)}&feature=fm-radio`,
      external: false
    },
    { 
      label: `${state} Drive-By Christmas Lights`, 
      url: `/displays?state=${encodeURIComponent(state)}&type=drive-by`,
      external: false
    },
  ];
}

/**
 * Render a link with proper accessibility and routing
 */
const SeoLinkComponent: React.FC<{ link: SeoLink; className?: string }> = ({ 
  link, 
  className = "text-primary hover:underline focus:underline focus:outline-hidden focus:ring-2 focus:ring-primary/20 rounded-sm" 
}) => {
  if (link.external) {
    return (
      <a 
        href={link.url} 
        className={className}
        rel="noopener noreferrer"
        target="_blank"
        aria-label={`${link.label} (opens in new tab)`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <Link 
      to={link.url} 
      className={className}
      aria-label={link.label}
    >
      {link.label}
    </Link>
  );
};

const DisplaySeoFooter: React.FC<DisplaySeoFooterProps> = ({ 
  city: propCity, 
  state: propState, 
  zip: propZip, 
  latitude, 
  longitude, 
  displayId,
  location 
}) => {
  // Use enhanced location parser with fallback to props
  const locationComponents = React.useMemo(() => {
    return location ? getSeoLocationComponents(location) : null;
  }, [location]);
  
  const city = locationComponents?.isValid ? locationComponents.city : propCity;
  const state = locationComponents?.isValid ? locationComponents.stateAbbreviation : propState;
  const zip = locationComponents?.isValid ? locationComponents.zipCode : propZip;

  // Fetch nearby displays with error handling (always call hooks first)
  const { data, isLoading, error } = useNearbyDisplays({ 
    latitude, 
    longitude, 
    excludeDisplayId: displayId, 
    radiusMiles: 60 
  });

  // Generate link collections with error handling
  const linkCollections = React.useMemo(() => {
    try {
      const nearbyCities = data?.cities || [];
      
      return {
        bestOf: getBestOfLinks(city, state),
        browseNearby: getBrowseNearbyLinks(city, state, zip),
        trending: getTrendingLinks(state),
        otherPlaces: getNearbyCityLinks(nearbyCities, city, state)
      };
    } catch (err) {
      console.error('Error generating SEO links:', err);
      return {
        bestOf: [],
        browseNearby: [],
        trending: [],
        otherPlaces: []
      };
    }
  }, [city, state, zip, data?.cities]);

  // Validate required location data after hooks
  if (!city || !state) {
    return null;
  }

  return (
    <footer 
      className="bg-muted border-t border-border py-10 mt-10"
      role="contentinfo"
      aria-label="Related light display links"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Section A: Best of City, State */}
          <section aria-labelledby="best-of-heading">
            <h3 
              id="best-of-heading"
              className="font-semibold text-lg mb-3 text-foreground"
            >
              Best of {city}, {state}
            </h3>
            {linkCollections.bestOf.length > 0 ? (
              <nav aria-label={`Best light displays in ${city}, ${state}`}>
                <ul className="space-y-2" role="list">
                  {linkCollections.bestOf.map((link, index) => (
                    <li key={`best-of-${index}`} role="listitem">
                      <SeoLinkComponent link={link} />
                    </li>
                  ))}
                </ul>
              </nav>
            ) : (
              <p className="text-muted-foreground text-sm">No featured displays available</p>
            )}
          </section>

          {/* Section B: Browse Nearby */}
          <section aria-labelledby="browse-nearby-heading">
            <h3 
              id="browse-nearby-heading"
              className="font-semibold text-lg mb-3 text-foreground"
            >
              Browse Nearby
            </h3>
            {linkCollections.browseNearby.length > 0 ? (
              <nav aria-label={`Browse light displays near ${city}`}>
                <ul className="space-y-2" role="list">
                  {linkCollections.browseNearby.map((link, index) => (
                    <li key={`browse-nearby-${index}`} role="listitem">
                      <SeoLinkComponent link={link} />
                    </li>
                  ))}
                </ul>
              </nav>
            ) : (
              <p className="text-muted-foreground text-sm">No nearby displays found</p>
            )}
          </section>

          {/* Section C: Trending Searches in State */}
          <section aria-labelledby="trending-heading">
            <h3 
              id="trending-heading"
              className="font-semibold text-lg mb-3 text-foreground"
            >
              Trending in {state}
            </h3>
            {linkCollections.trending.length > 0 ? (
              <nav aria-label={`Trending light display searches in ${state}`}>
                <ul className="space-y-2" role="list">
                  {linkCollections.trending.map((link, index) => (
                    <li key={`trending-${index}`} role="listitem">
                      <SeoLinkComponent link={link} />
                    </li>
                  ))}
                </ul>
              </nav>
            ) : (
              <p className="text-muted-foreground text-sm">No trending searches available</p>
            )}
          </section>

          {/* Section D: Other Places Nearby */}
          <section aria-labelledby="other-places-heading">
            <h3 
              id="other-places-heading"
              className="font-semibold text-lg mb-3 text-foreground"
            >
              Other Places Nearby
            </h3>
            {isLoading ? (
              <div className="text-muted-foreground text-sm" role="status" aria-live="polite">
                Loading nearby cities...
              </div>
            ) : error ? (
              <div className="text-muted-foreground text-sm" role="alert">
                Unable to load nearby locations
              </div>
            ) : linkCollections.otherPlaces.length > 0 ? (
              <nav aria-label="Light displays in other nearby cities">
                <ul className="space-y-2" role="list">
                  {linkCollections.otherPlaces.map((link, index) => (
                    <li key={`other-places-${index}`} role="listitem">
                      <SeoLinkComponent link={link} />
                    </li>
                  ))}
                </ul>
              </nav>
            ) : (
              <p className="text-muted-foreground text-sm">No nearby cities found</p>
            )}
          </section>
        </div>
      </div>
    </footer>
  );
};

export default DisplaySeoFooter; 